import { Injectable, signal, WritableSignal, computed } from '@angular/core';

type IdSelector<T> = (item: T) => string | number;

interface CollectionStore<T> {
  items: WritableSignal<ReadonlyArray<T>>;
  index: Map<string, T>;
  idSelector: IdSelector<T>;
}

@Injectable({ providedIn: 'root' })
export class DataStoreService {
  private stores = new Map<string, CollectionStore<any>>();

  // Selector por defecto: usa .id (string o number)
  private defaultIdSelector<T>(item: T): string | number {
    if (item && typeof item === 'object' && 'id' in item) {
      const idVal = (item as any).id;
      if (typeof idVal === 'string' || typeof idVal === 'number') {
        return idVal;
      }
    }
    throw new Error(
      'No se encontró una propiedad id (string o number) y no se pasó idSelector.'
    );
  }

  private ensureStore<T>(
    key: string,
    idSelector?: IdSelector<T>
  ): CollectionStore<T> {
    if (!this.stores.has(key)) {
      this.stores.set(key, {
        items: signal<ReadonlyArray<T>>([]),
        index: new Map<string, T>(),
        idSelector: idSelector ?? this.defaultIdSelector,
      });
    } else if (idSelector) {
      this.stores.get(key)!.idSelector = idSelector;
    }
    return this.stores.get(key)!;
  }

  hydrate<T>(
    key: string,
    items: ReadonlyArray<T>,
    idSelector?: IdSelector<T>
  ): void {
    const store = this.ensureStore<T>(key, idSelector);
    const copy = [...items];
    const idx = new Map<string, T>();
    for (const it of copy) {
      idx.set(String(store.idSelector(it)), it);
    }
    store.items.set(copy);
    store.index = idx;
  }

  upsertOne<T>(key: string, item: T, idSelector?: IdSelector<T>): void {
    const store = this.ensureStore<T>(key, idSelector);
    const id = String(store.idSelector(item));
    const current = store.items();
    const pos = current.findIndex((i) => String(store.idSelector(i)) === id);
    if (pos >= 0) {
      const next = [...current];
      next[pos] = item;
      store.items.set(next);
    } else {
      store.items.set([...current, item]);
    }
    store.index.set(id, item);
  }

  upsertMany<T>(
    key: string,
    items: ReadonlyArray<T>,
    idSelector?: IdSelector<T>
  ): void {
    for (const it of items) this.upsertOne<T>(key, it, idSelector);
  }

  removeOne<T>(
    key: string,
    id: string | number,
    idSelector?: IdSelector<T>
  ): void {
    const store = this.ensureStore<T>(key, idSelector);
    const idStr = String(id);
    const next = store
      .items()
      .filter((i) => String(store.idSelector(i)) !== idStr);
    store.items.set(next);
    store.index.delete(idStr);
  }

  clear<T>(key: string, idSelector?: IdSelector<T>): void {
    const store = this.ensureStore<T>(key, idSelector);
    store.items.set([]);
    store.index.clear();
  }

  selectAll<T>(key: string, idSelector?: IdSelector<T>) {
    const store = this.ensureStore<T>(key, idSelector);
    return computed(() => store.items());
  }

  getById<T>(
    key: string,
    id: string | number,
    idSelector?: IdSelector<T>
  ): T | undefined {
    const store = this.ensureStore<T>(key, idSelector);
    return store.index.get(String(id));
  }

  selectById<T>(key: string, id: string | number, idSelector?: IdSelector<T>) {
    const store = this.ensureStore<T>(key, idSelector);
    const idStr = String(id);
    return computed(() => store.index.get(idStr));
  }
}
